/**
 * Claim status enums and selector option arrays.
 *
 * Both list-relevant enums (lifted in S20a) and edit-only enums
 * (lifted in S20b) live here. The legacy `_legacy/components/Claims/
 * constants.js` shim re-exports from this module until S20b deletes
 * it.
 */
import i18next from 'i18next';
import { ReactComponent as PendingIcon } from 'assets/icons/pending.svg';
import { ReactComponent as ShippedIcon } from 'assets/icons/shipped.svg';
import { ReactComponent as ProcessingIcon } from 'assets/icons/processing.svg';
import { ReactComponent as HoldIcon } from 'assets/icons/hold.svg';

export const CLAIMS_STATUSES = {
  invited: 'invited',
  inProgress: 'inProgress',
  pendingReview: 'pendingReview',
  completed: 'completed',
  processing: 'processing',
  scheduled: 'scheduled',
};

export const ENCOUNTER_CLAIMS_STATUSES = {
  draft: 'draft',
  notCreated: 'notCreated',
  completed: 'completed',
};

export const BILLING_CLAIMS_STATUSES = {
  billed: 'billed',
  pending: 'pending',
  icdIssue: 'icdIssue',
  insuranceIssue: 'insuranceIssue',
  appointmentCancelled: 'appointmentCancelled',
  readyToBill: 'readyToBill',
  appointmentTypeIssue: 'appointmentTypeIssue',
  hold: 'hold',
};

export const CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS = [
  {
    label: 'Program',
    value: 'program',
  },
  {
    label: 'Assessment',
    value: 'assessment',
  },
  {
    label: 'Resource',
    value: 'resource',
  },
  {
    label: 'Preventive Health Counseling',
    value: 'intellyChart',
  },
  {
    label: 'BHI Webhook',
    value: 'bhi-webhook',
  },
];

export const CLAIM_STATUS_SELECTOR_OPTIONS = [
  {
    label: 'Billed',
    value: 'billed',
  },
  {
    label: 'Patient Responsibility',
    value: 'patientResponsibility',
  },
  {
    label: 'In Process',
    value: 'inProcess',
  },
  {
    label: 'Paid',
    value: 'paid',
  },
  {
    label: 'Partially Paid',
    value: 'partiallyPaid',
  },
  {
    label: 'Denied',
    value: 'denied',
  },
  {
    label: 'Resubmitted',
    value: 'resubmitted',
  },
  {
    label: 'Not Billable',
    value: 'notBillable',
  },
  {
    label: 'Pending',
    value: 'pending',
  },
];

export const ENCOUNTER_CLAIMS_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:draft'),
    value: ENCOUNTER_CLAIMS_STATUSES.draft,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:notCreated'),
    value: ENCOUNTER_CLAIMS_STATUSES.notCreated,
    icon: ProcessingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:completed'),
    value: ENCOUNTER_CLAIMS_STATUSES.completed,
    icon: ShippedIcon,
    enabledForNonSponsors: true,
  },
];

export const BILLING_CLAIMS_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:billed'),
    value: BILLING_CLAIMS_STATUSES.billed,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:pending'),
    value: BILLING_CLAIMS_STATUSES.pending,
    icon: PendingIcon,
    enabledForNonSponsors: true,
    addDivider: true,
  },
  {
    label: i18next.t('tables:hold'),
    value: BILLING_CLAIMS_STATUSES.hold,
    icon: PendingIcon,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:icdIssue'),
    value: BILLING_CLAIMS_STATUSES.icdIssue,
    icon: ProcessingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:insuranceIssue'),
    value: BILLING_CLAIMS_STATUSES.insuranceIssue,
    icon: ProcessingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:appointmentTypeIssue'),
    value: BILLING_CLAIMS_STATUSES.appointmentTypeIssue,
    icon: ProcessingIcon,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:appointmentCancelled'),
    value: BILLING_CLAIMS_STATUSES.appointmentCancelled,
    icon: ShippedIcon,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:readyToBill'),
    value: BILLING_CLAIMS_STATUSES.readyToBill,
    icon: ShippedIcon,
    enabledForNonSponsors: true,
  },
];

// Edit-only enums (lifted from _legacy/components/Claims/constants.js in S20b).
export const PAYMENT_METHOD_CLAIMS_SELECTOR_OPTIONS = [
  { label: 'Check', value: 'check' },
  { label: 'ACH', value: 'ach' },
  { label: 'CASH', value: 'cash' },
  { label: 'Credit Card', value: 'creditCard' },
];

export const PR_TYPE_CLAIMS_SELECTOR_OPTIONS = [
  { label: 'Copay', value: 'copay' },
  { label: 'Coinsurance', value: 'coinsurance' },
  { label: 'Deductible', value: 'deductible' },
];

export const CPT_STATUS_SELECTOR_OPTIONS = [
  { label: 'Billed', value: 'billed' },
  { label: 'Patient Responsibility', value: 'patientResponsibility' },
  { label: 'In Process', value: 'inProcess' },
  { label: 'Paid', value: 'paid' },
  { label: 'Denied', value: 'denied' },
  { label: 'Resubmitted', value: 'resubmitted' },
  { label: 'Not Billable', value: 'notBillable' },
];

export const CLAIM_STATUS_SELECTOR_STATUSES = {
  billed: 'billed',
  patientResponsibility: 'patientResponsibility',
  inProcess: 'inProcess',
  paid: 'paid',
  partiallyPaid: 'partiallyPaid',
  denied: 'denied',
  resubmitted: 'resubmitted',
  notBillable: 'notBillable',
  pending: 'pending',
};

export const BILLING_CLAIMS_SELECTOR_OPTIONS = [
  { label: 'Billed', value: 'billed' },
  { label: 'Pending', value: 'pending' },
  { label: 'ICD Issue', value: 'icdIssue' },
  { label: 'Insurance Issue', value: 'insuranceIssue' },
  { label: 'Appt. Cancelled', value: 'appointmentCancelled' },
  { label: 'Ready for Billing', value: 'readyToBill' },
  { label: 'Appt. Type Issue', value: 'appointmentTypeIssue' },
  { label: 'Hold', value: 'hold' },
];
